import { EMPTY_VALUE, type Money } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, HStack, Icon, Link, Separator, Tag, Text, ToggleTip, VStack } from '@betterment/samba-react';
import { formatDate } from 'date-fns/format';

import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { formatMoneySafe } from '~/b4b/utils/formatHelpers';
import { urlBuilder } from '~/b4b/utils/urlBuilder';
import { PlanStatus, TrendDirection } from '~/graphql/schema';

import { PlanInsightsDocument } from './graphql/query.planInsights.generated';

import type { IconColorToken, IconName, TextColorToken } from '@betterment/samba-react';

const trendIconName = (trend: TrendDirection): IconName => {
  if (trend === TrendDirection.Up) return 'UpArrow';
  if (trend === TrendDirection.Down) return 'DownArrow';
  return 'Minus';
};

const trendTextColor = (trend: TrendDirection): TextColorToken => {
  if (trend === TrendDirection.Up) return 'text.success';
  if (trend === TrendDirection.Down) return 'text.warning';
  return 'text.weak';
};

const trendIconColor = (trend: TrendDirection): IconColorToken => {
  if (trend === TrendDirection.Up) return 'icon.success';
  if (trend === TrendDirection.Down) return 'icon.warning';
  return 'icon.weak';
};

type MetricItemProps = {
  label: string;
  value: string;
  tooltip?: string;
  changeSummary?: string | null;
  trend?: TrendDirection | null;
};

const PlanStatusTag = ({ status, isConversionPlan }: { status?: PlanStatus; isConversionPlan?: boolean }) => {
  if (status !== PlanStatus.Onboarding) return null;
  if (isConversionPlan) {
    return (
      <Tag accent="blue">
        <Tag.Icon name="ArrowCircle" />
        <Tag.Text>Conversion transfer in progress</Tag.Text>
      </Tag>
    );
  }
  return (
    <Tag accent="blue">
      <Tag.Text>Plan onboarding in progress</Tag.Text>
    </Tag>
  );
};

const MetricItem = ({ label, value, tooltip, changeSummary, trend }: MetricItemProps) => (
  <VStack css={{ gap: 'space.50', alignItems: 'flex-start' }}>
    <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
      <Text variant="text50">{label}</Text>
      {tooltip && (
        <ToggleTip>
          <ToggleTip.Trigger iconName="InfoCircle" label={`${label} info`} />
          <ToggleTip.Content css={{ maxW: '300px' }} side="right" align="start">
            <Text variant="text50">{tooltip}</Text>
          </ToggleTip.Content>
        </ToggleTip>
      )}
    </HStack>
    <Text css={{ textStyle: { base: 'text200', md: 'text300' } }}>{value}</Text>
    {changeSummary && trend && (
      <HStack>
        <Icon name={trendIconName(trend)} size="xsmall" color={trendIconColor(trend)} />
        <Text variant="text50" color={trendTextColor(trend)}>
          {changeSummary}
        </Text>
      </HStack>
    )}
  </VStack>
);

export const PlanInsights = () => {
  const employerId = useCurrentEmployerId();
  const { data } = useGraphQLQuery(PlanInsightsDocument, { variables: { employerId } });

  const plan = data?.employer?.plan;

  if (!plan) return null;

  const { insights, status, isConversionPlan, totalBalance } = plan;
  const asOfDate = plan.deconversionDate ?? plan.terminationDate;

  return (
    <Card css={{ p: 'space.300' }}>
      <Card.Header>
        <HStack css={{ justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <VStack>
            <HStack css={{ gap: 'space.100' }}>
              <H2 variant="title200">Plan insights</H2>
              <PlanStatusTag status={status} isConversionPlan={isConversionPlan} />
            </HStack>
            {asOfDate && <Text variant="text50">{`As of ${formatDate(asOfDate, 'MM/dd/yy')}`}</Text>}
          </VStack>
          <Link href={urlBuilder.plan()} variant="textPrimary" css={{ display: { base: 'none', lg: 'block' } }}>
            View plan details
          </Link>
        </HStack>
      </Card.Header>
      <Separator css={{ my: 'space.300' }} />
      <Box css={{ display: 'grid', gridTemplateColumns: { base: '1fr', md: '1fr 1fr' }, gap: 'space.300' }}>
        <MetricItem
          label="Plan balance"
          value={
            status === PlanStatus.Onboarding || status === PlanStatus.Terminated || status === PlanStatus.Deconverted
              ? EMPTY_VALUE
              : formatMoneySafe(totalBalance as Money)
          }
        />
        <MetricItem
          label="Year-to-date contributions"
          value={formatMoneySafe(insights?.yearToDateContributions as Money)}
        />
        <MetricItem
          label="Participation rate"
          value={insights?.participationRate ? `${insights.participationRate.percent}%` : EMPTY_VALUE}
          tooltip="Percentage of all eligible employees who contributed more than $0 to their 401(k) in the past month."
          changeSummary={insights?.participationRate?.changeSummary}
          trend={insights?.participationRate?.trend}
        />
        <MetricItem
          label="Avg. contribution rate"
          value={insights?.deferralRate ? `${insights.deferralRate.percent}%` : EMPTY_VALUE}
          tooltip="Calculated by dividing all eligible employee contributions over the past month by total gross compensation for the same period."
          changeSummary={insights?.deferralRate?.changeSummary}
          trend={insights?.deferralRate?.trend}
        />
      </Box>
      <Separator css={{ display: { base: 'block', lg: 'none' }, mt: 'space.300' }} />
      <Link
        href={urlBuilder.plan()}
        variant="textPrimary"
        css={{ display: { base: 'block', lg: 'none' }, mt: 'space.300' }}
      >
        View plan details
      </Link>
    </Card>
  );
};
